import { Input, type InputProps } from '../Input/Input';

/** §5: a native `date`, or a native `time` where a time is what is being captured. */
export const DATE_INPUT_TYPES = ['date', 'time'] as const;
export type DateInputType = (typeof DATE_INPUT_TYPES)[number];

export interface DateInputProps extends Omit<InputProps, 'type'> {
    readonly type?: DateInputType;
    /** ISO `YYYY-MM-DD` for a date, `HH:mm` for a time — the format the native control reads. */
    readonly min?: string;
    readonly max?: string;
}

/**
 * §5's Date input: the native control, "styled to the Input row above" — which is literally what
 * this is, a composition over `Input` rather than a second input with its own copy of the chrome.
 *
 * It is still its own component, for two reasons that are about dates rather than about styling.
 * It narrows `type` to the two the row names, so `<DateInput type="datetime-local">` — which no
 * §5 row specifies and which renders differently in every browser — is not reachable by accident.
 * And it is where `DateRangeInput` lives, which is the rule §5 actually spends its words on: a
 * range is **two labelled inputs**, never a custom two-month popover.
 *
 * The browser renders the value in the user's own locale format, which §5 calls correct behaviour
 * rather than a deviation — the *display* convention (`Fri 18 Sep`, §9) governs text we render, not
 * the control's own editing surface.
 */
export function DateInput({ type = 'date', ...rest }: DateInputProps) {
    return <Input {...rest} type={type} />;
}
